/* eslint-disable consistent-return */
import { useEffect, useRef } from 'react';

type UseOverlayFocusOptions = {
  /** The ref to the element to focus when the overlay is opened. */
  initialFocusRef?: React.RefObject<HTMLElement>;
  /** The ref to the element to focus when the overlay is closed. */
  returnFocusRef?: React.RefObject<HTMLElement | null>;
  /** Whether the overlay is currently showing. */
  isShowing: boolean;
  /** Whether the focus-visible behavior is enabled on mount. */
  showFocusRing: boolean;
};
/**
 * A hook that manages focus when an overlay opens and closes.
 *
 * When an overlay opens:
 * - It focuses the element specified by initialFocusRef, or falls back to the root element
 * - It remembers the element that had focus before the overlay opened
 *
 * When an overlay closes:
 * - It returns focus to the element that had focus before the overlay opened (or returnFocusRef if specified)
 * - This creates a smooth focus experience for keyboard and screen reader users
 *
 * The hook also respects elements with autoFocus within the overlay and won't override them.
 */

export function useOverlayFocus(
  overlayRef: React.RefObject<HTMLElement | null>,
  {
    initialFocusRef,
    returnFocusRef,
    isShowing,
    showFocusRing,
  }: UseOverlayFocusOptions
) {
  const sourceElementRef = useRef<HTMLElement>(undefined);

  useEffect(() => {
    if (isShowing && overlayRef.current) {
      const isActiveElementInOverlay = overlayRef.current.contains(
        document.activeElement
      );

      // Only focus if the active element is not already in the floating element. This let's us
      // support things like `autoFocus` on input elements or buttons.
      if (!isActiveElementInOverlay) {
        const focusElement = initialFocusRef?.current || overlayRef.current;
        focusElement.focus();
      }

      const focusedElement = document.activeElement;

      // If the focus ring is enabled, we need to add a data-focus-visible attribute to the focused element
      // and remove it when the focus moves out of the element.
      if (showFocusRing && focusedElement) {
        focusedElement.setAttribute('data-focus-visible', 'true');
        focusedElement.addEventListener('focusout', () => {
          focusedElement.removeAttribute('data-focus-visible');
        });
      }

      const returnFocusElement =
        returnFocusRef?.current || sourceElementRef.current;

      return () => {
        sourceElementRef.current = undefined;
        returnFocusElement?.focus();
      };
    }
  }, [isShowing, initialFocusRef, returnFocusRef, overlayRef, showFocusRing]);

  function onFocus(event: React.FocusEvent<HTMLElement>) {
    if (event.relatedTarget && !sourceElementRef.current) {
      sourceElementRef.current = event.relatedTarget as HTMLElement;
    }
  }

  return {
    onFocus,
  };
}
